/**
 * Solitaire Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-draw-mode
 * stats including best times, fewest moves, and streaks.
 *
 * Follows SudokuStatisticsService pattern.
 */

import {
  SolitaireStatistics,
  SolitaireGameResult,
  SolitaireDrawModeStats,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class SolitaireStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'solitaire_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): SolitaireStatistics {
    return {
      draw1: this.createEmptyDrawModeStats(),
      draw3: this.createEmptyDrawModeStats(),
      totalGames: 0,
      totalPlayTime: 0,
      currentStreak: 0,
      bestStreak: 0,
    };
  }

  private createEmptyDrawModeStats(): SolitaireDrawModeStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      bestTime: null,
      averageTime: 0,
      winPercentage: 0,
      fewestMoves: null,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): SolitaireStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as SolitaireStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get solitaire statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(result: SolitaireGameResult, userId?: string): boolean {
    try {
      const stats = this.getStatistics(userId);
      const modeKey = result.drawMode === 1 ? 'draw1' : 'draw3';
      const modeStats = stats[modeKey];

      modeStats.gamesPlayed++;

      if (result.completed) {
        modeStats.gamesWon++;

        // Update best time
        if (modeStats.bestTime === null || result.time < modeStats.bestTime) {
          modeStats.bestTime = result.time;
        }

        // Update fewest moves
        if (modeStats.fewestMoves === null || result.moveCount < modeStats.fewestMoves) {
          modeStats.fewestMoves = result.moveCount;
        }

        // Update average time
        const totalTime = modeStats.averageTime * (modeStats.gamesWon - 1) + result.time;
        modeStats.averageTime = Math.round(totalTime / modeStats.gamesWon);

        // Update streak
        stats.currentStreak++;
        if (stats.currentStreak > stats.bestStreak) {
          stats.bestStreak = stats.currentStreak;
        }
      } else {
        stats.currentStreak = 0;
      }

      modeStats.winPercentage =
        modeStats.gamesPlayed > 0
          ? (modeStats.gamesWon / modeStats.gamesPlayed) * 100
          : 0;

      stats.totalGames++;
      stats.totalPlayTime += result.time;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record solitaire game result:', error);
      return false;
    }
  }

  /** Get statistics for a specific draw mode. */
  getDrawModeStatistics(
    drawMode: 1 | 3,
    userId?: string
  ): SolitaireDrawModeStats {
    const stats = this.getStatistics(userId);
    return drawMode === 1 ? stats.draw1 : stats.draw3;
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset solitaire statistics:', error);
      return false;
    }
  }
}
